import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Bike, Globe, LogOut, User, Package } from "lucide-react";
import { useBrand } from "../lib/brand";
import { NotificationBell } from "./NotificationBell";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

const LANG_LABELS = { fr: "Français", en: "English", de: "Deutsch", es: "Español" };

const ROLE_META = {
  passenger: { label: "Passager", icon: User, route: "/passenger" },
  driver: { label: "Chauffeur", icon: Bike, route: "/driver" },
};

export function DashboardSideNav({ tabs, active, onSelect }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, switchRole } = useAuth();
  const { firstWord, Icon } = useBrand();

  const roles = (user?.roles || (user?.role ? [user.role] : [])).filter((r) => r === "passenger" || r === "driver");
  const doSwitch = async (role) => {
    if (role === user?.role) return;
    try {
      await switchRole(role);
      navigate(ROLE_META[role].route);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de changer de profil.");
    }
  };

  const _isNold = typeof window !== "undefined" && /noldgo\./i.test(window.location.hostname);
  const _langs = (!_isNold && typeof localStorage !== "undefined" && localStorage.getItem("bsg_zone") === "africa") ? ["fr", "en"] : ["fr", "en", "de", "es"];
  const _cur = (i18n.language || "fr").slice(0, 2);
  const lang = (_langs.includes(_cur) ? _cur : "fr").toUpperCase();

  return (
    <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-72 bg-[#1A1A1A] border-r border-white/5 z-40">
      <button data-testid="brand-logo-dash" onClick={() => navigate("/")} className="flex items-center gap-2 px-6 py-6">
        <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
        </span>
        <span className="font-head font-extrabold text-2xl tracking-tight text-white">
          {firstWord}<span className="text-[#F26419]"> Go</span>
        </span>
      </button>

      <nav className="flex flex-col gap-1 px-3 mt-2">
        {roles.length > 1 && (
          <div data-testid="role-switcher" className="mb-3 mx-1 p-1 rounded-2xl bg-white/5 border border-white/10 flex gap-1">
            {roles.map((r) => {
              const M = ROLE_META[r];
              const isCur = user?.role === r;
              return (
                <button
                  key={r}
                  data-testid={`role-switch-${r}`}
                  onClick={() => doSwitch(r)}
                  className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-xl text-sm font-bold transition-colors ${isCur ? "bg-[#F26419] text-white" : "text-white/70 hover:text-white hover:bg-white/5"}`}
                >
                  <M.icon className="w-4 h-4" /> {M.label}
                </button>
              );
            })}
          </div>
        )}
        {tabs.map((tb) => {
          const isActive = active === tb.id;
          return (
            <button
              key={tb.id}
              data-testid={`sidenav-tab-${tb.id}`}
              onClick={() => onSelect(tb.id)}
              className={`group flex items-center gap-3 h-12 px-4 rounded-full text-[15px] font-semibold transition-colors ${
                isActive ? "bg-[#F2EDE4] text-[#1A1A1A] shadow-sm" : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              <tb.icon className={`w-5 h-5 shrink-0 ${isActive ? "text-[#D94F2B]" : "text-white/70 group-hover:text-white"}`} strokeWidth={2.2} />
              <span className="truncate">{tb.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="mt-auto px-4 pb-6 pt-4 space-y-3">
        <div className="flex items-center gap-2">
          <NotificationBell />
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-toggle-dash"
                className="flex-1 flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/25 transition-colors text-sm font-semibold text-white"
              >
                <Globe className="w-4 h-4" /> {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {_langs.map((l) => (
                <DropdownMenuItem key={l} data-testid={`lang-option-${l}`} onClick={() => setTimeout(() => i18n.changeLanguage(l), 0)}>
                  {LANG_LABELS[l] || l.toUpperCase()}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-3 px-1 py-1">
          <span className="w-9 h-9 rounded-xl bg-[#F26419] text-white flex items-center justify-center font-head font-bold shrink-0">{(user?.name || "?")[0].toUpperCase()}</span>
          <div className="min-w-0">
            <div className="text-white text-sm font-semibold truncate">{user?.name || user?.phone}</div>
            <div className="text-white/50 text-xs truncate">{user?.phone}</div>
          </div>
        </div>
        <button
          data-testid="logout-btn-dash"
          onClick={() => { logout(); navigate("/"); }}
          className="w-full h-11 rounded-xl bg-white/5 border border-white/10 hover:border-[#D32F2F] hover:text-[#D32F2F] text-white font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          <LogOut className="w-4 h-4" /> {t("common.logout")}
        </button>
      </div>
    </aside>
  );
}
